import { getTestAttributes, slug } from '@chillibean/component-library';
import { PropTypes } from 'prop-types';
import { useState, useRef, useEffect } from 'react';

import { getFieldTitle } from '../../../__util__/field';
import { regexParser } from '../../../account/administration/FormTemplates/utils/utils';
import withInputLabel from '../hoc/withInputLabel';
import FormattedCharacterInput from './FormattedCharacterInput';

export const PLACEHOLDER_CHARACTER = '·';

/* Field with validation and char limit */
export function FormattedField(props) {
  const {
    values,
    disabled,
    handleChange,
    handleBlur,
    handleClick,
    fieldID,
    fieldRequired,
    fieldIdentifier = 'formatted',
    fieldName,
    fieldText,
    showError,
    errorMessage,
    inputFormat,
    charLimit,
  } = props;

  const [showFocusShadow, setShowFocusShadow] = useState(false);
  const inputRefs = useRef({});
  const inputOuterContainerRef = useRef(null);
  const inputInnerContainerRef = useRef(null);
  const [inputContainerWidths, setInputContainersWidths] = useState({});
  const [currentInput, setCurrentInput] = useState(null);

  const getFieldValue = (value, regex) => {
    if (!value || value === PLACEHOLDER_CHARACTER) {
      return '';
    }
    if (regex === '-') {
      return '-';
    }
    return value;
  };

  const splitRegex = regexParser(inputFormat, charLimit);
  const valuesAsSplitObject = splitRegex.reduce((object, regex, index) => {
    return {
      ...object,
      [index]: {
        regex,
        value: getFieldValue(values[index], regex),
        inputNumber: index,
      },
    };
  }, {});

  useEffect(() => {
    setInputContainersWidths({
      innerContainer: inputInnerContainerRef.current?.offsetWidth,
      outerCountainer: inputOuterContainerRef.current?.offsetWidth,
    });
  }, []);

  /*
  - Get last input number, of the filtered non dash inputs (the last indexed input)
  */
  const calculateLastInputNumber = (valuesObject) => {
    return Math.max(
      ...Object.values(valuesObject)
        .filter((e) => e.regex !== '-')
        .map((e) => e.inputNumber),
    );
  };

  const reconstructedValueArray = (event, index) => {
    const targetValue = event.target.value;
    const valueObject = {
      ...valuesAsSplitObject,
      [index]: { ...valuesAsSplitObject[index], value: targetValue },
    };
    const valuesArray = Object.keys(valueObject).reduce(
      (array, key) => [...array, valueObject[key].regex == '-' ? '-' : valueObject[key].value],
      [],
    );
    return valuesArray;
  };

  /*
    - Conditional: Verify that it is not the last element and the current input is not the last input
    - Get the next input in line skipping dashes and put it in focus
  */
  const handleChangeFocus = (event, index, currentInputNumber) => {
    const lastInputNumber = calculateLastInputNumber(valuesAsSplitObject);

    if (currentInputNumber !== lastInputNumber) {
      const followingFields = Object.values(valuesAsSplitObject)
        .filter((inputValue) => inputValue.inputNumber > currentInputNumber)
        .filter((inputValue) => inputValue.regex !== '-');
      const nextFieldNumber = followingFields.shift()?.inputNumber;
      const nextInput = inputRefs.current[nextFieldNumber];
      event.currentTarget.value !== '' && nextInput.focus() && nextInput.select();
    }
  };

  /*
    - Get the previous input in line skipping dashes and put it in focus
  */
  const handleOnKeyDown = (event, index, currentInputNumber) => {
    if (currentInputNumber !== 0) {
      const previousFields = Object.values(valuesAsSplitObject)
        .filter((inputValue) => inputValue.inputNumber < currentInputNumber)
        .filter((inputValue) => inputValue.regex !== '-');
      const previousFieldNumber = previousFields.pop()?.inputNumber;
      const previousInput = inputRefs.current[previousFieldNumber];
      event.currentTarget.value === '' && previousInput.focus() && previousInput.select();
    }
  };

  const handleOnChange = (event, index, currentInputNumber) => {
    setCurrentInput(currentInputNumber);
    handleChangeFocus(event, index, currentInputNumber);
    handleChange && handleChange(event, reconstructedValueArray(event, index));
  };

  const handleOnBlur = (event) => {
    setShowFocusShadow(false);
    handleBlur && handleBlur(values);
  };

  const handleOnClick = (event) => {
    setShowFocusShadow(true);
    handleClick && handleClick(event);
  };

  return (
    <div className="form__field form__field--custom">
      {fieldText && <p>{getFieldTitle(fieldRequired, fieldText)}</p>}
      <div
        className={`form__control form__control--custom ${showError ? 'form__control--validation-error' : ''} ${
          showFocusShadow ? 'formatted-input__container--shadow' : ''
        }`}
        tabIndex="0"
        onBlur={(event) => handleOnBlur(event)}
        ref={inputOuterContainerRef}
      >
        <div
          className="formatted-input__container"
          ref={inputInnerContainerRef}
          {...getTestAttributes(`${slug(fieldIdentifier)}-form-field`)}
        >
          {Object.keys(valuesAsSplitObject).map((key, index) => (
            <FormattedCharacterInput
              key={`${fieldID}-${key}`}
              index={key}
              characterObject={valuesAsSplitObject[key]}
              onChange={(event, index) => handleOnChange(event, index, valuesAsSplitObject[key].inputNumber)}
              onKeyDown={(event, index) => handleOnKeyDown(event, index, valuesAsSplitObject[key].inputNumber)}
              onClick={handleOnClick}
              disabled={disabled}
              inputRef={(el) => (inputRefs.current[index] = el)}
            />
          ))}
        </div>
      </div>
      {inputContainerWidths.innerContainer > inputContainerWidths.outerCountainer &&
        currentInput !== calculateLastInputNumber(valuesAsSplitObject) && <div className="overflow-fade" />}
      {showError && (
        <div className="form__validation-error" data-test-id={`${slug(fieldIdentifier)}-form-field-error-message`}>
          {errorMessage}
        </div>
      )}{' '}
    </div>
  );
}

FormattedField.propTypes = {
  values: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleClick: PropTypes.func,
  handleKeyDown: PropTypes.func,
  fieldID: PropTypes.string,
  fieldRequired: PropTypes.bool,
  fieldIdentifier: PropTypes.string,
  fieldName: PropTypes.string,
  fieldText: PropTypes.string,
  showError: PropTypes.bool,
  errorMessage: PropTypes.string,
  inputFormat: PropTypes.string.isRequired,
  charLimit: PropTypes.number.isRequired,
};

export default withInputLabel(FormattedField);
